import {
  Component,
  computed,
  Injectable,
  inject,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  model,
} from '@angular/core';
import { DateAdapter, MatNativeDateModule } from '@angular/material/core';
import {
  DateRange,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepickerModule,
  MatDateRangeSelectionStrategy,
} from '@angular/material/datepicker';
import { TranslocoPipe } from '@jsverse/transloco';

import { DatePickerRange, DatepickerPanel, Ucum } from '@treetop/shared/model';

import { ButtonDirective } from '../../button/button.directive';
import { DatepickerHeaderComponent } from '../datepicker-header/datepicker-header.component';

@Injectable()
class DatepickerRangeSelectionStrategy implements MatDateRangeSelectionStrategy<Date> {
  private _dateAdapter = inject(DateAdapter) as DateAdapter<Date>;

  selectionFinished(date: Date | null, currentRange: DateRange<Date>): DateRange<Date> {
    const { start, end } = currentRange;
    if (start === null) {
      return new DateRange<Date>(date, null);
    }
    if (end === null) {
      const [rangeStart, rangeEnd] = date && this._dateAdapter.compareDate(date, start) >= 0 ? [start, date] : [date, start];
      return new DateRange<Date>(rangeStart, rangeEnd);
    }
    return new DateRange<Date>(date, null);
  }

  createPreview(activeDate: Date | null, currentRange: DateRange<Date>): DateRange<Date> {
    if (currentRange.start && !currentRange.end && activeDate) {
      const [start, end] =
        this._dateAdapter.compareDate(activeDate, currentRange.start) >= 0
          ? [currentRange.start, activeDate]
          : [activeDate, currentRange.start];
      return new DateRange<Date>(start, end);
    }
    return new DateRange<Date>(null, null);
  }
}

@Component({
  selector: 'tt-datepicker-content',
  standalone: true,
  templateUrl: './datepicker-content.component.html',
  styleUrls: ['./datepicker-content.component.scss'],
  imports: [MatDatepickerModule, TranslocoPipe, MatNativeDateModule, ButtonDirective],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DatepickerRangeSelectionStrategy,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerContentComponent {
  public showRangeSelector = model(true);
  public showCalendar = model(true);
  public showCustomRangeSelector = model(false);
  public minDate = model<Date>();
  public selected = model<Date | Ucum>(undefined);
  public rangeStart = model<Date | null>(null);
  public rangeEnd = model<Date | null>(null);

  @ViewChild(TemplateRef) template!: TemplateRef<unknown>;

  public datepicker: DatepickerPanel;
  protected readonly DatePickerHeaderComponent = DatepickerHeaderComponent;

  protected selectedRange = computed(() => new DateRange<Date>(this.rangeStart(), this.rangeEnd()));

  protected startAt = computed<Date>(() => {
    const rangeStart = this.rangeStart();
    if (rangeStart) return rangeStart;
    const selected = this.selected();
    const parsedDate = Date.parse(selected as string);
    return !isNaN(parsedDate) ? new Date(parsedDate) : new Date();
  });

  protected selectedDate = computed<Date>(() => {
    const selected = this.selected();
    const parsedDate = Date.parse(selected as string);
    return !isNaN(parsedDate) ? new Date(parsedDate) : undefined;
  });

  protected selectedChange($event: Date) {
    if ($event !== this.selected()) {
      this.selected.set($event);
      this.datepicker.selectedDateChanged($event);
    }
  }

  protected ucumSelectedChange($event: Ucum) {
    if ($event !== this.selected()) {
      this.selected.set($event);
      this.datepicker.selectedDateChanged($event);
    }
  }

  protected onRangeCalendarChange(event: Date | DateRange<Date>): void {
    if (event instanceof DateRange) {
      const range = event as DateRange<Date>;
      this.rangeStart.set(range.start);
      this.rangeEnd.set(range.end ?? null);
      if (range.start && range.end) {
        this.datepicker.selectedRangeChanged({ start: range.start, end: range.end } satisfies DatePickerRange);
      }
      return;
    }

    const currentStart = this.rangeStart();
    const currentEnd = this.rangeEnd();

    if (!currentStart || currentEnd !== null) {
      this.rangeStart.set(event);
      this.rangeEnd.set(null);
    } else {
      const [start, end] = event >= currentStart ? [currentStart, event] : [event, currentStart];
      this.rangeStart.set(start);
      this.rangeEnd.set(end);
      this.datepicker.selectedRangeChanged({ start, end } satisfies DatePickerRange);
    }
  }
}
